import { useState, useEffect, useRef } from "react";
import { Snowflake, Mountains, Warning, GraduationCap, ThumbsUp, ThumbsDown, Lightbulb, Scales } from "@phosphor-icons/react";

const SECTIONS = [
  { id: "good-vs-bad", label: "Good vs Bad", icon: Scales },
  { id: "snowball-vs-avalanche", label: "Snowball vs Avalanche", icon: Snowflake },
  { id: "cc-traps", label: "Credit Card Traps", icon: Warning },
  { id: "student-loans", label: "Student Loans", icon: GraduationCap },
  { id: "pro-tips", label: "Pro Tips", icon: Lightbulb },
];

function Card({ title, icon: Icon, accent = "amber", children }) {
  return (
    <div className={`rounded-sm border border-zinc-800 bg-zinc-900/40 p-5 hover:border-${accent}-500/30 transition-colors`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} weight="bold" className={`text-${accent}-400`} />
        <h3 className="font-heading text-sm font-bold text-zinc-100 tracking-tight">{title}</h3>
      </div>
      <div className="text-[12px] leading-relaxed text-zinc-400 space-y-2">{children}</div>
    </div>
  );
}

export default function DebtDestroyer() {
  const [active, setActive] = useState(SECTIONS[0].id);
  const refs = useRef({});

  // Highlight the section in view as the user scrolls
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the section nearest the top that's visible
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) setActive(visible[0].target.id);
      },
      { rootMargin: "-100px 0px -60% 0px", threshold: 0.01 }
    );
    SECTIONS.forEach(s => {
      const el = refs.current[s.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = refs.current[id];
    if (!el) return;
    const main = el.closest("[data-testid='main-content']") || window;
    const top = el.getBoundingClientRect().top + (main === window ? window.scrollY : main.scrollTop) - 170;
    if (main === window) window.scrollTo({ top, behavior: "smooth" });
    else main.scrollTo({ top, behavior: "smooth" });
    setActive(id);
  };

  return (
    <div className="px-5 py-6 max-w-[1200px]" data-testid="debt-destroyer-page">
      {/* Hero */}
      <div className="rounded-sm border border-red-500/30 bg-gradient-to-br from-red-500/10 via-zinc-900/40 to-zinc-950 p-6 mb-5">
        <div className="flex items-center gap-3 mb-3">
          <Mountains size={28} weight="fill" className="text-red-400" />
          <h2 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Debt Destroyer</h2>
        </div>
        <p className="text-[13px] text-zinc-400 leading-relaxed max-w-2xl">
          Debt isn't moral. It's mathematical. <span className="text-red-300">Used badly, it consumes decades of your future.</span>
          Used well, it builds wealth faster than cash ever could. The trick is knowing which is which — then attacking
          the bad kind with a system, not a vibe.
        </p>
      </div>

      {/* Sticky sub-nav — offset below parent FinancialEducation tab bar (~100px) */}
      <div className="sticky top-[100px] z-10 -mx-5 px-5 py-2.5 bg-zinc-950/95 backdrop-blur-sm border-b border-zinc-800/60 mb-5" data-testid="debt-subnav">
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide">
          {SECTIONS.map(s => {
            const Icon = s.icon;
            const isActive = active === s.id;
            return (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                data-testid={`debt-subnav-${s.id}`}
                className={`shrink-0 inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-sm border transition-colors whitespace-nowrap ${
                  isActive
                    ? "bg-red-500/15 border-red-500/40 text-red-300"
                    : "bg-zinc-900/40 border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700"
                }`}
              >
                <Icon size={12} weight="bold" />
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-6">
        {/* Good vs Bad */}
        <section id="good-vs-bad" ref={el => (refs.current["good-vs-bad"] = el)} className="scroll-mt-[170px]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title="Good debt — debt that earns" icon={ThumbsUp} accent="emerald">
              <p>Borrowed money used to buy an asset that <strong className="text-zinc-200">appreciates, produces income, or raises your earning power</strong> — at an interest rate well below the asset's return.</p>
              <ul className="list-disc pl-5 space-y-1.5 my-2">
                <li><strong className="text-zinc-200">Mortgage on a primary home</strong> — 6–7% rate vs long-run housing 4–5% + lifetime housing locked in.</li>
                <li><strong className="text-zinc-200">Rental property loan</strong> — tenant pays the principal; you keep the appreciation.</li>
                <li><strong className="text-zinc-200">Business loan</strong> — borrowed at 8% to make 30% return on the business.</li>
                <li><strong className="text-zinc-200">Federal student loans for a high-ROI degree</strong> — engineering, nursing, trades, etc.</li>
              </ul>
              <p className="text-[11px] text-zinc-500 italic">Test: would you take this loan if you didn't have to? If yes — it's a tool.</p>
            </Card>

            <Card title="Bad debt — debt that consumes" icon={ThumbsDown} accent="red">
              <p>Borrowed money used to buy <strong className="text-zinc-200">depreciating things or lifestyle</strong> — usually at high interest.</p>
              <ul className="list-disc pl-5 space-y-1.5 my-2">
                <li><strong className="text-zinc-200">Credit card balances</strong> — 22–29% APR. Mathematically the worst.</li>
                <li><strong className="text-zinc-200">Auto loans on luxury cars</strong> — depreciates 20% the day you drive off.</li>
                <li><strong className="text-zinc-200">Buy-now-pay-later for clothes/gadgets</strong> — debt you forgot you signed up for.</li>
                <li><strong className="text-zinc-200">Personal loans for vacations</strong> — paying for a memory at 12% interest.</li>
                <li><strong className="text-zinc-200">Payday loans</strong> — 300–400% effective APR. Avoid at all costs.</li>
              </ul>
              <p className="text-[11px] text-zinc-500 italic">Test: would the lender exist if humans were rational? Bad debt sells you the dopamine, then collects for years.</p>
            </Card>
          </div>
        </section>

        {/* Snowball vs Avalanche */}
        <section id="snowball-vs-avalanche" ref={el => (refs.current["snowball-vs-avalanche"] = el)} className="scroll-mt-[170px]">
          <Card title="Two payoff strategies — Snowball vs Avalanche" icon={Snowflake} accent="blue">
            <p className="mb-3">List every debt — balance, minimum payment, APR. Pay minimums on all. Throw <strong className="text-zinc-200">every extra dollar</strong> at one target. Which target? Pick a method:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-4 rounded-sm bg-blue-500/5 border border-blue-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Snowflake size={14} weight="fill" className="text-blue-400" />
                  <p className="font-heading text-sm font-bold text-blue-300">Snowball — smallest balance first</p>
                </div>
                <ul className="list-disc pl-5 space-y-1 text-[11px]">
                  <li>Sort debts by <strong className="text-zinc-200">balance</strong>, smallest to largest.</li>
                  <li>Kill the smallest. Roll its payment onto the next one. Repeat.</li>
                  <li>You'll knock out 2–3 small debts in the first months — momentum is the entire point.</li>
                  <li><strong className="text-emerald-300">Best for:</strong> people who need wins to stay motivated. Behavioural &gt; mathematical.</li>
                </ul>
              </div>
              <div className="p-4 rounded-sm bg-amber-500/5 border border-amber-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Mountains size={14} weight="fill" className="text-amber-400" />
                  <p className="font-heading text-sm font-bold text-amber-300">Avalanche — highest APR first</p>
                </div>
                <ul className="list-disc pl-5 space-y-1 text-[11px]">
                  <li>Sort debts by <strong className="text-zinc-200">interest rate</strong>, highest to lowest.</li>
                  <li>Kill the highest-rate debt. Roll the payment down. Repeat.</li>
                  <li>Saves you the <strong className="text-zinc-200">most money mathematically</strong> — fewer total interest dollars paid.</li>
                  <li><strong className="text-emerald-300">Best for:</strong> people who're motivated by spreadsheets, not feelings.</li>
                </ul>
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 italic mt-3">
              <strong className="text-zinc-300">Hybrid trick:</strong> if your highest APR is also a manageable balance, you get both — knock out one painful debt fast AND save the most interest. The best plan is the one you'll actually finish.
            </p>
          </Card>
        </section>

        {/* CC traps */}
        <section id="cc-traps" ref={el => (refs.current["cc-traps"] = el)} className="scroll-mt-[170px]">
          <Card title="Credit card traps to avoid" icon={Warning} accent="red">
            <ul className="list-disc pl-5 space-y-2">
              <li><strong className="text-zinc-200">Minimum payments are designed to keep you stuck.</strong> A $5,000 balance at 24% paying minimums takes ~22 years and costs ~$8,500 in interest. Always pay above the minimum.</li>
              <li><strong className="text-zinc-200">Cash advances</strong> — interest starts the second you withdraw. No grace period. Often 5% upfront fee + 25% APR.</li>
              <li><strong className="text-zinc-200">"Deferred interest" promotions</strong> — common on store cards/medical. If you don't pay it off in full by the deadline, ALL the interest from day one gets back-charged.</li>
              <li><strong className="text-zinc-200">Balance transfers</strong> can help — but only if you have a real plan to clear it before the 0% promo ends. Otherwise the rate snaps back to 24%+ on whatever's left.</li>
              <li><strong className="text-zinc-200">Treating credit limits as available money.</strong> A $10k limit isn't $10k you have — it's $10k of rope.</li>
            </ul>
          </Card>
        </section>

        {/* Student loans */}
        <section id="student-loans" ref={el => (refs.current["student-loans"] = el)} className="scroll-mt-[170px]">
          <Card title="Student loans — the long game" icon={GraduationCap} accent="violet">
            <p>Student loans deserve their own playbook because they're often six-figure, low-rate, long-duration debt with unique federal protections.</p>
            <ul className="list-disc pl-5 space-y-1.5 my-2">
              <li><strong className="text-zinc-200">Federal vs private — know which you have.</strong> Federal loans have income-driven repayment, deferment options, and forgiveness programs. Private loans have none of that.</li>
              <li><strong className="text-zinc-200">Don't refinance federal → private just for a lower rate.</strong> You permanently lose forbearance, IDR plans, and PSLF eligibility. Read the fine print.</li>
              <li><strong className="text-zinc-200">Consider IDR plans</strong> if income is low — payments cap at 10–20% of discretionary income, with forgiveness after 20–25 years.</li>
              <li><strong className="text-zinc-200">PSLF (Public Service Loan Forgiveness)</strong> — 10 years of qualifying payments at a non-profit/government job → remaining balance forgiven, tax-free.</li>
              <li><strong className="text-zinc-200">Avalanche method usually wins</strong> for high-interest private student loans. For low-interest federal loans (3–5%), invest the spare cash and pay minimums — the math favors compounding.</li>
            </ul>
          </Card>
        </section>

        {/* Pro tips */}
        <section id="pro-tips" ref={el => (refs.current["pro-tips"] = el)} className="scroll-mt-[170px]">
          <Card title="Pro tips" icon={Lightbulb} accent="amber">
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong className="text-zinc-200">Track every debt in one place</strong> — balance, APR, minimum payment, payoff date. You can't kill what you can't see.</li>
              <li><strong className="text-zinc-200">Negotiate APRs</strong> — call the card issuer once a year. "I've been a good customer, can you lower my rate?" Works ~30% of the time.</li>
              <li><strong className="text-zinc-200">Stop adding new debt while paying off old debt</strong> — sounds obvious; people fail this constantly.</li>
              <li><strong className="text-zinc-200">Build the $1,000 emergency starter fund first</strong> so the next surprise doesn't land on a credit card.</li>
            </ul>
          </Card>
        </section>
      </div>
    </div>
  );
}
